/**
 * SESAAT.MY - AI Service (Secure Cloud Version)
 * 
 * ALL API calls now go through Firebase Cloud Functions.
 * No API keys are exposed in the browser.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

// Cloud Function references
const transcribeTikTokFn = httpsCallable(functions, 'transcribeTikTok');
const rewriteScriptFn = httpsCallable(functions, 'rewriteScript');
const generatePromptsFn = httpsCallable(functions, 'generatePrompts');
const generateProductContentFn = httpsCallable(functions, 'generateProductContent', { timeout: 300000 });
const generateFromIntelligenceFn = httpsCallable(functions, 'generateFromIntelligence', { timeout: 300000 });
const generateLibraryIntelligenceFn = httpsCallable(functions, 'generateLibraryIntelligence', { timeout: 180000 });
const transcribeLocalFileFn = httpsCallable(functions, 'transcribeLocalFile');
const createPaymentFn = httpsCallable(functions, 'createPayment');
const getMyDailyQuotaFn = httpsCallable(functions, 'getMyDailyQuota');
const deductFlownityTrialCoinFn = httpsCallable(functions, 'deductFlownityTrialCoin');
const matchAgentOutputFn = httpsCallable(functions, 'matchAgentOutput', { timeout: 120000 });

// ============================================
// ERROR HANDLING HELPER
// ============================================
function handleCloudError(error) {
    console.error('Cloud Function Error:', error);

    const errorMessages = {
        'unauthenticated': 'Sila log masuk terlebih dahulu.',
        'permission-denied': error.message || 'Anda tidak mempunyai akses untuk fungsi ini.',
        'resource-exhausted': error.message || 'Limit harian anda telah habis. Cuba lagi esok!',
        'invalid-argument': error.message || 'Data yang dimasukkan tidak sah.',
        'not-found': 'Video tidak dijumpai. Mungkin private atau telah dipadam.',
        'deadline-exceeded': 'Request timeout. Sila cuba lagi.',
        'internal': error.message || 'Ralat server. Sila cuba lagi sebentar.',
        'failed-precondition': error.message || 'Syarat tidak dipenuhi.',
        'unavailable': 'Perkhidmatan tidak tersedia buat masa ini.'
    };

    const code = error.code?.replace('functions/', '') || 'internal';
    const message = errorMessages[code] || error.message || 'Ralat tidak diketahui.';

    // Kekalkan kod pada Error yang dilempar — pemanggil perlukan ia untuk bezakan jenis
    // kegagalan (cth 'unavailable' = gangguan pihak kita → sorok butang Retry).
    const err = new Error(message);
    err.code = code;
    throw err;
}

// ============================================
// AI SERVICE OBJECT
// ============================================
export const aiService = {
    // Quota lookup. Returns { tier, used, cap, remaining, unlimited? } or null on error.
    // Don't surface error to user — quota fetch failure must not block generation.
    async getMyDailyQuota() {
        try {
            const result = await getMyDailyQuotaFn();
            return result.data;
        } catch (error) {
            console.warn('[Quota] fetch failed:', error.message);
            return null;
        }
    },

    // FLOWNITY trial coin — potong 1 coin percubaan SEBELUM scrape (server yang putuskan mode).
    // Return { allowed: true, mode: 'admin'|'full'|'trial', remaining? } — admin/full tak potong apa-apa.
    // Throw Error dengan mesej BM dari server (coin habis / tiada langganan) — caller kena alert + abort.
    async deductFlownityTrialCoin() {
        try {
            const result = await deductFlownityTrialCoinFn();
            return result.data;
        } catch (error) {
            handleCloudError(error);
        }
    },

    /**
     * Transcribe TikTok video via Cloud Function
     * Supports: standard URLs, short URLs (vm.tiktok.com)
     */
    async transcribeTikTok(url) {
        try {
            const result = await transcribeTikTokFn({ url });
            return result.data;
        } catch (error) {
            handleCloudError(error);
        }
    },

    /**
     * Rewrite script with AI via Cloud Function
     */
    async rewriteScript(originalScript, style, useTimestamps = true) {
        try {
            const result = await rewriteScriptFn({
                script: originalScript,
                style,
                useTimestamps
            });
            return result.data.script;
        } catch (error) {
            handleCloudError(error);
        }
    },

    /**
     * Generate Image and Video prompts via Cloud Function
     */
    async generatePrompts(script) {
        try {
            const result = await generatePromptsFn({ script });
            return result.data;
        } catch (error) {
            handleCloudError(error);
        }
    },

    /**
     * Transcribe local audio/video file via Cloud Function
     * File is converted to base64 and sent to server
     * API key is now HIDDEN server-side
     */
    async transcribeWithGroq(audioFile) {
        try {
            // Convert file to base64 for transport
            const base64 = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(audioFile);
            });

            const result = await transcribeLocalFileFn({
                fileBase64: base64,
                fileName: audioFile.name,
                mimeType: audioFile.type
            });

            return result.data;
        } catch (error) {
            handleCloudError(error);
        }
    },

    /**
     * Generate content for products via Cloud Function  
     * API key is now HIDDEN server-side
     * Supports vision (image analysis) + text generation
     * 
     * @param {Object} product - Product data (name, link, imageBase64, etc.)
     * @param {Object} persona - Logic option with sets[] array
     * @param {Function} onProgress - Callback: (currentIndex, totalSets, setLabel, phase) => void
     * @returns {Object} Combined result per set
     */
    async generateProductContent(product, persona, onProgress, options = {}) {
        try {
            const { avatar = null, moduleId = null, testInstruction = null, template = null, templateId = null, sceneCount = null, subModuleId = null, proMode = false } = options;

            const isJsonMode = !!persona.jsonMode;

            // ★ Server-side master prompt: if moduleId or testInstruction given, skip
            // packing client-side instruction — server reads from Firestore / uses test text.
            const serverResolves = !!(moduleId || testInstruction);

            // Get sets from persona (only used when server does NOT resolve)
            let personaSets = [];
            if (!serverResolves) {
                if (isJsonMode) {
                    const masterInstruction = persona.combinedInstruction ||
                        (persona.sets && persona.sets.length > 0 ? persona.sets[0].instruction : persona.instruction) || '';
                    personaSets = [{ id: 'master', targetSet: 'set1', label: 'Master', instruction: masterInstruction }];
                } else {
                    personaSets = persona.sets && persona.sets.length > 0
                        ? persona.sets
                        : (persona.instruction
                            ? [{ id: 'set1', targetSet: persona.targetSet || 'set1', label: 'Set 1', instruction: persona.instruction }]
                            : []);
                }

                if (personaSets.length === 0 || !personaSets[0].instruction) {
                    throw new Error('Logic option tidak mempunyai sebarang instruction.');
                }
            }

            // Generate progress ID for real-time tracking via Firestore
            const progressId = `gen_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

            // Report progress: analyzing image (Step 1) or researching (if no image)
            if (onProgress) {
                const hasImg = !!(product.imageBase64 || product.imageUrl);
                const totalSets = serverResolves ? 1 : (isJsonMode ? 1 : personaSets.length);
                onProgress(0, totalSets,
                    hasImg ? 'Analyzing product image...' : 'Researching product...',
                    hasImg ? 'analyzing_image' : 'researching', progressId);
            }

            // Send to Cloud Function with client-side safety timeout
            // Cloud Function has 300s server timeout, but if connection hangs
            // the UI would stay stuck forever. This 5-minute client timeout
            // ensures the overlay always closes with an error message.
            const cloudFnPromise = generateProductContentFn({
                product: {
                    id: product.id || null,
                    group: product.group || null,
                    name: product.name,
                    link: product.link,
                    price: product.price,
                    originalPrice: product.originalPrice || null,
                    discount: product.discount || null,
                    imageBase64: product.imageBase64 || null,
                    imageUrl: product.imageUrl || null,
                    gender: product.gender || null
                },
                avatar: avatar ? {
                    id: avatar.id || null,
                    age: avatar.age || null,
                    hijab: typeof avatar.hijab === 'boolean' ? avatar.hijab : null
                } : null,
                // ★ When moduleId or testInstruction is set, server resolves master prompt
                // from Firestore and personaSets is ignored. Keeps master prompt secret.
                moduleId: moduleId || null,
                testInstruction: testInstruction || null,
                template: template || null,
                templateId: templateId || null,
                // ★ Pro Mode (PLAN_PROSTUDIO_BLOCKS.md): server bina master prompt
                //   daripada blocks pelanggan (users/{uid}/proBlocks/{moduleId}).
                //   Client hanya menghantar TANDA — teks blocks dibaca server sendiri.
                proMode: !!proMode,
                sceneCount: sceneCount || null,
                subModuleId: subModuleId || null,
                personaSets: serverResolves ? [] : personaSets.map(s => ({
                    id: s.id,
                    targetSet: s.targetSet,
                    label: s.label,
                    instruction: s.instruction
                })),
                jsonMode: isJsonMode,
                progressId
            });

            const clientTimeout = new Promise((_, reject) =>
                setTimeout(() => reject(new Error(
                    'Creative generation took too long (timeout 5 min). Sila cuba lagi.'
                )), 5 * 60 * 1000)
            );

            const result = await Promise.race([cloudFnPromise, clientTimeout]);

            // Report progress: done
            if (onProgress) {
                const totalSets = serverResolves ? 1 : personaSets.length;
                onProgress(totalSets, totalSets, 'Complete', 'parsing');
            }

            return result.data;
        } catch (error) {
            handleCloudError(error);
        }
    },

    /**
     * Generate content using pre-provided Intelligence data (skip Groq API)
     * Uses only OpenAI gpt-5-mini for creative generation.
     * Reusable for AI8SAAT and AI16SAAT.
     * 
     * @param {Object} product - Product data (name, link, price)
     * @param {Object} intelligenceData - Pre-provided { imageAnalysis, webResearch }
     * @param {Object} persona - Logic option with sets[] array
     * @param {Function} onProgress - Callback: (currentIndex, totalSets, setLabel, phase, progressId) => void
     * @returns {Object} Combined result per set
     */
    async generateFromIntelligence(product, intelligenceData, persona, onProgress, options = {}) {
        try {
            const { avatar = null, moduleId = null, testInstruction = null, template = null, templateId = null, sceneCount = null, subModuleId = null, proMode = false } = options;
            const isJsonMode = !!persona.jsonMode;
            const serverResolves = !!(moduleId || testInstruction);

            let personaSets = [];
            if (!serverResolves) {
                if (isJsonMode) {
                    const masterInstruction = persona.combinedInstruction ||
                        (persona.sets && persona.sets.length > 0 ? persona.sets[0].instruction : persona.instruction) || '';
                    personaSets = [{ id: 'master', targetSet: 'set1', label: 'Master', instruction: masterInstruction }];
                } else {
                    personaSets = persona.sets && persona.sets.length > 0
                        ? persona.sets
                        : (persona.instruction
                            ? [{ id: 'set1', targetSet: persona.targetSet || 'set1', label: 'Set 1', instruction: persona.instruction }]
                            : []);
                }

                if (personaSets.length === 0 || !personaSets[0].instruction) {
                    throw new Error('Logic option tidak mempunyai sebarang instruction.');
                }
            }

            const progressId = `gen_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

            // Report progress: skip analyze/research, go straight to generating
            if (onProgress) {
                const totalSets = serverResolves ? 1 : (isJsonMode ? 1 : personaSets.length);
                onProgress(0, totalSets,
                    'Generating from Intelligence data...',
                    'generating', progressId);
            }

            const cloudFnPromise = generateFromIntelligenceFn({
                product: {
                    id: product.id || null,
                    group: product.group || null,
                    name: product.name,
                    link: product.link,
                    price: product.price,
                    originalPrice: product.originalPrice || null,
                    discount: product.discount || null,
                    gender: product.gender || null
                },
                avatar: avatar ? {
                    id: avatar.id || null,
                    age: avatar.age || null,
                    hijab: typeof avatar.hijab === 'boolean' ? avatar.hijab : null
                } : null,
                intelligenceData: intelligenceData,
                // ★ When moduleId or testInstruction set, server resolves master prompt from Firestore
                moduleId: moduleId || null,
                testInstruction: testInstruction || null,
                template: template || null,
                templateId: templateId || null,
                // ★ Pro Mode (PLAN_PROSTUDIO_BLOCKS.md): server bina master prompt
                //   daripada blocks pelanggan (users/{uid}/proBlocks/{moduleId}).
                //   Client hanya menghantar TANDA — teks blocks dibaca server sendiri.
                proMode: !!proMode,
                sceneCount: sceneCount || null,
                subModuleId: subModuleId || null,
                personaSets: serverResolves ? [] : personaSets.map(s => ({
                    id: s.id,
                    targetSet: s.targetSet,
                    label: s.label,
                    instruction: s.instruction
                })),
                jsonMode: isJsonMode,
                progressId
            });

            const clientTimeout = new Promise((_, reject) =>
                setTimeout(() => reject(new Error(
                    'Creative generation took too long (timeout 5 min). Sila cuba lagi.'
                )), 5 * 60 * 1000)
            );

            const result = await Promise.race([cloudFnPromise, clientTimeout]);

            if (onProgress) {
                const totalSets = serverResolves ? 1 : personaSets.length;
                onProgress(totalSets, totalSets, 'Complete', 'parsing');
            }

            return result.data;
        } catch (error) {
            handleCloudError(error);
        }
    },

    /**
     * Create payment session (Stripe Checkout)
     */
    async createPayment(plan = 'monthly') {
        try {
            const result = await createPaymentFn({ plan });
            return result.data;
        } catch (error) {
            handleCloudError(error);
        }
    },

    /**
     * Admin-only: Pre-cache Stage 1+2 intelligence onto a library item.
     * @param {string} folderId - Library folder (e.g. 'product')
     * @param {string} itemId - Library item ID
     * @param {boolean} forceRefresh - true = bypass cache, regenerate fresh
     * @returns {Promise<{ok, itemId, imageAnalysisLength, webResearchLength, partial, durationMs}>}
     */
    async generateLibraryIntelligence({ folderId, itemId, forceRefresh = false }) {
        try {
            const result = await generateLibraryIntelligenceFn({ folderId, itemId, forceRefresh });
            return result.data;
        } catch (error) {
            handleCloudError(error);
        }
    },

    /**
     * Padankan jawapan MENTAH custom GPT pelanggan dengan senarai field mereka.
     * Dipanggil SEKALI per agent semasa "Configure Agent" di PRO Studio — bukan
     * setiap produk. Pulangan: { sources[], mapping{fieldId:source|null},
     * setsDetected, scenesDetected, note, truncated }.
     */
    async matchAgentOutput({ rawText, fields, setCount, sceneCount }) {
        const result = await matchAgentOutputFn({ rawText, fields, setCount, sceneCount });
        return result.data;
    },

    /**
     * Format time helper
     */
    formatTime(seconds) {
        const min = Math.floor(seconds / 60);
        const sec = Math.floor(seconds % 60);
        return `${min}:${sec.toString().padStart(2, '0')}`;
    },

    /**
     * Extract TikTok ID (for display purposes only)
     */
    extractTikTokId(url) {
        try {
            const cleanUrl = url.split('?')[0];
            const match = cleanUrl.match(/\/video\/(\d+)/);
            return match?.[1] || null;
        } catch (error) {
            console.error('Error processing URL:', error);
            return null;
        }
    }
};
